/**
 * Recomendador de talla.
 *
 * No estima ni interpola: compara las medidas contra la misma tabla que la
 * clienta tiene a la vista. Si cintura y cadera no caen en la misma talla, o si
 * quedan fuera de la tabla, no inventa una respuesta: manda con la asesora.
 *
 * La tabla del fabricante deja huecos a proposito (por ejemplo, L termina en 85
 * cm de cintura y XL empieza en 87). Ahi el recomendador no elige: deriva a la
 * asesora, que es lo correcto cuando el dato no alcanza.
 */
(() => {
  // La tabla es nativa en centimetros con numeros redondos, asi que el borde
  // de cada talla es exacto. Lo que se convierte es lo que teclea la clienta.
  const CM_PER_IN = 2.54;
  const EPS = 0.001;

  const setup = (root) => {
    if (root.dataset.ecCalcReady === 'true') return;
    root.dataset.ecCalcReady = 'true';

    const data = root.querySelector('[data-ec-size-chart]');
    if (!data) return;

    /** @type {{size:string,wMin:number,wMax:number,hMin:number,hMax:number}[]} */
    let chart;
    try {
      chart = JSON.parse(data.textContent);
    } catch (_error) {
      return;
    }
    if (!chart.length) return;

    // El orden de la tabla ES el orden de tallas: XS ... 2XL.
    const orden = new Map(chart.map((row, i) => [row.size, i]));
    const laMayor = (a, b) => ((orden.get(a) ?? -1) >= (orden.get(b) ?? -1) ? a : b);
    const rellenar = (texto, pares) =>
      Object.entries(pares).reduce((t, [hueco, valor]) => t.split(hueco).join(valor), String(texto || ''));

    const form = root.querySelector('[data-ec-calc-form]');
    const waist = root.querySelector('[data-ec-calc-waist]');
    const hips = root.querySelector('[data-ec-calc-hips]');
    const out = root.querySelector('[data-ec-calc-result]');
    const copy = root.dataset;

    /** Dónde cae una medida: dentro de una talla, entre dos, o fuera. */
    const locate = (value, minKey, maxKey) => {
      const exact = chart.find((row) => value >= row[minKey] - EPS && value <= row[maxKey] + EPS);
      if (exact) return { kind: 'exact', size: exact.size };

      const first = chart[0];
      const last = chart[chart.length - 1];
      if (value < first[minKey]) return { kind: 'under', size: first.size };
      if (value > last[maxKey]) return { kind: 'over', size: last.size };

      // Cae en el hueco entre dos filas de la tabla.
      const below = [...chart].reverse().find((row) => row[maxKey] < value);
      const above = chart.find((row) => row[minKey] > value);
      return { kind: 'between', size: below?.size, next: above?.size };
    };

    /**
     * Marca en la tabla de la página la fila de la talla calculada.
     *
     * Sin esto la clienta recibe una letra («M») y tiene que buscarla a mano en
     * una tabla de seis filas para comprobar que le cuadra. Con la fila
     * resaltada, el resultado y su justificación se leen de una vez.
     */
    const marcarFila = (size) => {
      const filas = document.querySelectorAll('[data-ec-size-row]');
      filas.forEach((fila) => {
        const suya = size && fila.dataset.ecSizeRow === size;
        fila.classList.toggle('ec-guide-table__fila--tuya', Boolean(suya));
      });
    };

    const card = (tone, title, detail, size) => {
      marcarFila(tone === 'ask' ? null : size);
      const ask = copy.ecCalcWaHref
        ? `<a class="ec-size-calc__ask" href="${copy.ecCalcWaHref}" target="_blank" rel="noopener"
             data-ec-track="size_calc_help" data-ec-placement="size_guide">${copy.ecCalcAsk}</a>`
        : '';

      out.className = 'ec-size-calc__result ec-size-calc__result--' + tone;
      out.innerHTML = `
        <div class="ec-size-calc__head">
          ${size ? `<span class="ec-size-calc__size">${size}</span>` : ''}
          <div class="ec-size-calc__copy">
            ${size ? `<p class="ec-size-calc__label">${copy.ecCalcResultTitle}</p>` : ''}
            <p class="ec-size-calc__headline">${title}</p>
            ${detail ? `<p class="ec-size-calc__detail">${detail}</p>` : ''}
          </div>
        </div>
        <div class="ec-size-calc__actions">
          ${size && tone !== 'ask' ? `<button type="button" class="ec-size-calc__pick" data-ec-calc-pick="${size}">${copy.ecCalcPick}</button>` : ''}
          ${ask}
        </div>`;
      out.hidden = false;
    };

    // Un resultado viejo junto a medidas nuevas es peor que ninguno.
    const clear = () => {
      out.hidden = true;
      out.innerHTML = '';
      marcarFila(null);
    };
    [waist, hips].forEach((input) => input.addEventListener('input', clear));
    root.querySelectorAll('[name^="ec-calc-unit"]').forEach((radio) => radio.addEventListener('change', clear));

    form.addEventListener('submit', (event) => {
      event.preventDefault();

      const unitName = root.dataset.ecCalcUnitName || 'ec-calc-unit';
      const unit = root.querySelector(`[name="${unitName}"]:checked`)?.value || 'in';
      const factor = unit === 'cm' ? 1 : CM_PER_IN;
      const w = parseFloat(String(waist.value).replace(',', '.')) * factor;
      const h = parseFloat(String(hips.value).replace(',', '.')) * factor;

      if (!Number.isFinite(w) || !Number.isFinite(h) || w <= 0 || h <= 0) {
        card('ask', copy.ecCalcIncomplete, '', '');
        return;
      }

      const rw = locate(w, 'wMin', 'wMax');
      const rh = locate(h, 'hMin', 'hMax');

      if (rw.kind === 'exact' && rh.kind === 'exact' && rw.size === rh.size) {
        card('ok', copy.ecCalcOkNote, '', rw.size);
      } else if (rw.kind === 'over' || rh.kind === 'over' || rw.kind === 'under' || rh.kind === 'under') {
        card('ask', copy.ecCalcOut, copy.ecCalcOutNote, '');
      } else if (rw.kind === 'exact' && rh.kind === 'exact') {
        // Cintura y cadera en tallas distintas: se recomienda la MAYOR y se
        // dice por qué. No es una preferencia nuestra: la propia tabla de
        // Silueta de Oro indica escoger la mayor por la alta compresión.
        const size = laMayor(rw.size, rh.size);
        card(
          'mayor',
          rellenar(copy.ecCalcLargerTitle, { '%%': size }),
          rellenar(copy.ecCalcLargerWhy, { '%%W': rw.size, '%%H': rh.size }),
          size
        );
      } else {
        // Cae en el hueco que la tabla deja entre dos filas. Mismo criterio.
        const pair = [rw, rh].find((r) => r.kind === 'between');
        if (pair?.next) {
          card(
            'mayor',
            rellenar(copy.ecCalcLargerTitle, { '%%': pair.next }),
            rellenar(copy.ecCalcGapWhy, { '%%S': pair.size || '', '%%L': pair.next }),
            pair.next
          );
        } else {
          card('ask', copy.ecCalcBetween, '', '');
        }
      }

      const recomendada = out.querySelector('[data-ec-calc-pick]')?.dataset.ecCalcPick || '';
      window.Shopify?.analytics?.publish?.('eclipse_size_calc', {
        waist_result: rw.kind, hips_result: rh.kind, recommended: recomendada,
      });
    });

    // Marcar la talla recomendada en el selector de la ficha.
    out.addEventListener('click', (event) => {
      const button = event.target.closest('[data-ec-calc-pick]');
      if (!button) return;

      const size = button.dataset.ecCalcPick;
      const radio = document.querySelector(
        `variant-picker input[type="radio"][value="${CSS.escape(size)}"]`
      );
      if (!radio) return;

      radio.click();
      button.disabled = true;
      button.textContent = copy.ecCalcPicked;
    });
  };

  const init = (scope = document) => scope.querySelectorAll('[data-ec-size-calc]').forEach(setup);

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
